import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { ContactType } from '@/shared/lib/sj-orm';
import { BaseProps } from '@/shared/types';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { ContactTypeI18n } from './ContactTypeI18n';

type SelectContactTypeProps = BaseProps<{
  filter?: (type: ContactType) => boolean;
  onSelect: (type: ContactType) => void;
}>;

const contactTypesList = [
  ContactType.BANK,
  ContactType.BROKER,
  ContactType.INDIVIDUAL,
  ContactType.LEGAL_ENTITY,
];

export const SelectContactType = forwardRef<HTMLDivElement, SelectContactTypeProps>(
  ({ onSelect, filter, ...props }, ref) => {
    const { t } = useTranslation('entities/contacts');
    const items = useMemo(
      () => (filter ? contactTypesList.filter(filter) : contactTypesList),
      [filter],
    );

    return (
      <Container {...props} className="uikitScrollbarNone" ref={ref}>
        <Title>{t('selectContactTypeTitle')}</Title>
        <Group>
          {items.map((type, index) => (
            <Item onClick={() => onSelect(type)} key={index}>
              <ContactTypeI18n type={type} />
            </Item>
          ))}
        </Group>
      </Container>
    );
  },
);

SelectContactType.displayName = 'SelectContactType';

const Container = styled.div`
  width: 320px;
  padding: 4px;
  border-radius: 16px;
  border: 1px solid ${COLORS.systemDark100};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${COLORS.systemLight300};
`;

const Group = styled.div`
  padding: 4px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${COLORS.systemLight100};
`;

const Item = styled.div`
  height: 44px;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background: ${COLORS.systemLight200};
  }
`;

const Title = styled.div`
  font-size: 12px;
  color: ${COLORS.systemDark200};
  padding: 10px 0 0 12px;
`;
